import React, { ReactElement, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Button } from '../molecules';
import { validateEmail } from '../../utils/validators';
import { confirmSignUp, resendConfirmationCode } from '../../utils/auth-functions';
import errorStyling from '../../utils/error-styling';
import { GaAnalytics } from '../../utils/analytics';

type Props = {
  inApp: string | null;
  redirectUrl: string | null;
};

export default function ConfirmUserForm({ inApp, redirectUrl }: Props): ReactElement {
  const [email, setEmail] = useState<string | undefined>(undefined);
  const [emailIsValid, setEmailIsValid] = useState<boolean | undefined>(undefined);
  const [codeIsValid, setCodeIsValid] = useState<boolean | undefined>(undefined);
  const [code, setCode] = useState<string | undefined>(undefined);
  const [newCode, setNewCode] = useState<boolean>(false);
  const [response, setResponse] = useState<void | unknown>();
  const [error, setError] = useState<string | undefined>(undefined);
  const [formIsValid, setFormIsValid] = useState(false);
  const [status, setStatus] = useState<'initial' | 'touched'>('initial');

  const history = useHistory();
  const loginLink = redirectUrl ? `/login?confirmedUser=true&inApp=${inApp === 'true'}&redirectUrl=${redirectUrl}` : `/login?confirmedUser=true&inApp=${inApp === 'true'}`;

  useEffect(() => {
    if (formIsValid) {
      confirmSignUp({ username: email || '', code: typeof code === 'string' ? code : '' }).then((res) => {
        setResponse(res);
      });
    }
  }, [formIsValid]);

  const updateCode = (newVal: string) => {
    setCode(newVal);
  };
  useEffect(() => {
    setCodeIsValid(!!code);
  }, [code]);

  const handleConfirmSignUp = () => {
    setStatus('touched');
    if (validateEmail({ email }) === 'valid') setEmailIsValid(true);
    else setEmailIsValid(false);
    if (code && emailIsValid) {
      setFormIsValid(true);
    }
  };
  const [buttonAction, setButtonAction] = useState({ text: 'Conferma', action: () => handleConfirmSignUp() });
  useEffect(() => {
    if (emailIsValid !== undefined) {
      handleConfirmSignUp();
    }
  }, [emailIsValid]);

  const handleResponse = () => {
    if (typeof response === 'object' && response) {
      Object.keys(response).forEach((key) => {
        if (key === 'code') {
          const responseString = JSON.stringify(response);
          switch (true) {
            case /User is already confirmed/.test(String(response)):
            case /Current status is CONFIRMED/.test(String(response)):
              // provide a way out
              setButtonAction({
                text: 'Prosegui',
                action: () => history.replace(loginLink),
              });
              break;
            case /NotAuthorizedException/.test(responseString):
              setError('NotAuthorizedConfirm');
              break;
            case /LimitExceededException/.test(responseString):
              setError('LimitExceededException');
              break;
            case /CodeMismatchException/.test(responseString):
              setError('CodeMismatchException');
              break;
            case /UserNotFoundException/.test(responseString):
              setError('UserNotFoundException');
              break;

            default:
              break;
          }
        }
      });
    }
  };

  useEffect(() => {
    if (response === 'SUCCESS' && code) {
      GaAnalytics.sendEvent('register_end');
      history.replace(loginLink);
    }
    if (response === 'SUCCESS') {
      setNewCode(true);
    } else handleResponse();
  }, [response]);

  const handleResendConfirmation = () => {
    if (email && emailIsValid)
      resendConfirmationCode({ username: email }).then((res) => {
        setResponse(res);
      });
    if (validateEmail({ email }) === 'valid') setEmailIsValid(true);
    else setEmailIsValid(false);
  };

  return (
    <form id="verification-form">
      <div className="form-group">
        <input onChange={(e) => setEmail(e.target.value)} type="email" name="username" className="form-control w-100" placeholder="Email" />
      </div>
      {emailIsValid !== undefined && !emailIsValid && <div className="input-not-valid mb-2">Email non valida!</div>}

      <div className="form-group">
        <input onChange={(e) => updateCode(e.target.value)} type="text" name="verification_code" className="form-control" placeholder="Codice di verifica" />
        {codeIsValid === false && status == 'touched' && <div className="input-not-valid mb-1">Inserire il codice ricevuto via mail</div>}
      </div>

      <div className="d-flex flex-row justify-content-around align-items-baseline">
        <Button action={() => handleResendConfirmation()} className="btn link-override" data-action="resend_code" role="button">
          <u>Reinvia codice di verifica</u>
        </Button>
        <Button role="button" type={'submit'} action={buttonAction.action} className="btn override btn-primary mt-3 mb-2 text-center">
          {buttonAction.text}
        </Button>
      </div>
      {newCode && !code && <h5 className="text-center mt-4 p-0">Nuovo codice inviato correttamente!</h5>}
      {error !== undefined && !newCode && <div className="input-not-valid text-center m-0">{errorStyling(error)}</div>}
    </form>
  );
}
